

import { namespace } from "vuex-class"
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"

// @ts-ignore
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { Case, Field } from "@/types"

import api from "@/services/api"

import HubRecordViewService from "@/services/hubRecordViewService"

import SkeletonLoader from "../SkeletonLoader.vue"
import CaseList from "@/components/CaseList/CaseList.vue"
import { CurrentPageDetails, GetCasesForViewRequest } from "@/types/paginatedRequest"
import { CasesTable, CasesTables } from "@/types/case"
import { TABLE_RESULTS_LOADING_INDICATOR_TIMEOUT_MS } from "@/constants/insights"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-list": CaseList,
		"skeleton": SkeletonLoader,
		"v-select": vSelect
	}
})
export default class RecordView extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@caseStore.Action("detailsLoad")
	private detailsLoad!: () => void;

	@setup.Action loadFieldsIfUnloaded!: () => Promise<Field[]>

	views: any[] = [];

	activeView: any = null;
	loadingCases: boolean = true;
	errorLoading: boolean = false;
	errorUpdating: boolean = false;
	displayRecords: Case[] = [];
	private casesTables: CasesTables | null = null;
	private displayRecordTables: CasesTable[] = [];
	hubDashboardService: HubRecordViewService = new HubRecordViewService();

	private pageNumber: number = 1;
	private pageSize: number = 20;
	private latestTableResultsRequestIds: { [tableId: string]: number } = {};
	private pendingTableResultsRequestsByTable: { [tableId: string]: number } = {};
	private paginationLoadingIndicatorTimeouts: { [tableId: string]: number } = {};
	private visiblePaginationLoadingTableIds: string[] = [];

	public async mounted () {
		await this.loadFieldsIfUnloaded()
		this.onEditChanged()
	}

	public beforeDestroy () {
		Object.keys(this.paginationLoadingIndicatorTimeouts).forEach(tableId => {
			clearTimeout(this.paginationLoadingIndicatorTimeouts[tableId])
		})
		this.paginationLoadingIndicatorTimeouts = {}

		this.closeHubConnection()
	}

	/**
	 * Start the loading indicator for a table's results if they're taking a while.
	 * @param tableId The ID of the table to start the loading indicator for.
	 */
	private startTableResultsLoadingIndicator (tableId: string): void {
		const pendingCount = (this.pendingTableResultsRequestsByTable[tableId] ?? 0) + 1
		Vue.set(this.pendingTableResultsRequestsByTable, tableId, pendingCount)

		if (pendingCount !== 1) {
			return
		}

		const existingTimeout = this.paginationLoadingIndicatorTimeouts[tableId]
		if (existingTimeout !== undefined) {
			clearTimeout(existingTimeout)
		}

		const timeout = window.setTimeout(() => {
			if ((this.pendingTableResultsRequestsByTable[tableId] ?? 0) > 0 && !this.visiblePaginationLoadingTableIds.includes(tableId)) {
				this.visiblePaginationLoadingTableIds = [...this.visiblePaginationLoadingTableIds, tableId]
			}
			Vue.delete(this.paginationLoadingIndicatorTimeouts, tableId)
		}, TABLE_RESULTS_LOADING_INDICATOR_TIMEOUT_MS)

		Vue.set(this.paginationLoadingIndicatorTimeouts, tableId, timeout)
	}

	/**
	 * Stop the loading indicator for a table's results.
	 * @param tableId The ID of the table to stop the loading indicator for.
	 */
	private stopTableResultsLoadingIndicator (tableId: string): void {
		const pendingCount = (this.pendingTableResultsRequestsByTable[tableId] ?? 0) - 1
		if (pendingCount > 0) {
			Vue.set(this.pendingTableResultsRequestsByTable, tableId, pendingCount)
		} else {
			Vue.delete(this.pendingTableResultsRequestsByTable, tableId)
		}

		if ((this.pendingTableResultsRequestsByTable[tableId] ?? 0) > 0) {
			return
		}

		const timeout = this.paginationLoadingIndicatorTimeouts[tableId]
		if (timeout !== undefined) {
			clearTimeout(timeout)
			Vue.delete(this.paginationLoadingIndicatorTimeouts, tableId)
		}

		if (this.visiblePaginationLoadingTableIds.includes(tableId)) {
			this.visiblePaginationLoadingTableIds = this.visiblePaginationLoadingTableIds.filter(id => id !== tableId)
		}
	}

	public async rowClick (item: any) {
		this.$router.push({ path: `/cases/${item.caseNumber}` })
	}

	@Watch("edit")
	private async onEditChanged () {
		if (this.edit) {
			this.setEditMode()
		} else {
			this.setViewMode()
		}
	}

	private async setEditMode () {
		this.closeHubConnection()

		this.views = await api.caseViewsGet()
	}

	private async setViewMode (searchTerm: string = "") {
		this.loadingCases = true
		this.errorLoading = false

		try {
			if (this.value && this.value.viewId) {
				if (!this.activeView || this.activeView.id !== this.value.viewId) {
					api.caseViewsGet()
						.then(views => {
							this.views = views

							if (this.views) {
								this.activeView = this.views.find(v => v.id === this.value.viewId)
							}
						})
				}

				this.casesTables = await api.caseList(this.value.viewId, this.pageNumber, this.pageSize, this.casesTables?.id ?? "", searchTerm)
				this.displayRecordTables = this.casesTables.casesTables
			}
		} catch (err) {
			this.errorLoading = true
		}

		this.loadingCases = false
		await this.openHubConnection()
	}

	private async openHubConnection () {
		if (!this.value || !this.value.viewId) {
			return
		}

		try {
			const connection = await this.hubDashboardService.openConnection(this.value.viewId)

			if (connection) {
				connection.on("recordView", (data: Case[]) => {
					this.displayRecords = data
					this.errorUpdating = false
				})

				connection.on("recordViewError", (err: String) => {
					console.log(err)
					this.errorUpdating = true
				})
			}
		} catch (err) {
			this.errorUpdating = true
		}
	}

	private closeHubConnection () {
		if (!this.hubDashboardService) {
			return
		}

		this.hubDashboardService.closeConnection()
	}

	@Emit("input")
	private viewChanged (viewId: string) {
		return { ...this.value, viewId }
	}

	private async loadNextPage (details: CurrentPageDetails) {
		this.errorLoading = false
		const tableId = details.tableId ?? details.value
		const requestId = (this.latestTableResultsRequestIds[tableId] ?? 0) + 1
		Vue.set(this.latestTableResultsRequestIds, tableId, requestId)

		try {
			this.startTableResultsLoadingIndicator(tableId)
			if (!!this.value && !!this.value.viewId) {
				this.pageNumber = details.pageNumber ?? 1
				this.pageSize = details.pageSize ?? 20

				const request: GetCasesForViewRequest = {
					viewId: this.value.viewId,
					id: this.casesTables?.id ?? "",
					page: this.pageNumber,
					pageSize: this.pageSize,
					searchTerm: details.searchTerm,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					sortBy: details.sortBy,
					sortDesc: details.sortDesc,
					sortDataType: details.sortDataType,
					tableId: details.tableId
				}
				const casesTables = await api.caseListTable(request)
				if (requestId === this.latestTableResultsRequestIds[tableId]) {
					const casesTable = casesTables.casesTables.firstOrDefault((c: CasesTable) => c.value === tableId)
					const tableToUpdate = this.displayRecordTables.firstOrDefault((c: CasesTable) => c.value === tableId)

					if (tableToUpdate) {
						tableToUpdate.totalRecords = casesTable.totalRecords
						tableToUpdate.cases = [...casesTable.cases]
					}
				}
			}
		} catch (err) {
			console.error(err)
			if (requestId === this.latestTableResultsRequestIds[tableId]) {
				this.errorLoading = true
			}
		} finally {
			this.stopTableResultsLoadingIndicator(tableId)
		}
	}

	private async reloadBySearch (searchTerm: string = ""): Promise<void> {
		this.pageNumber = 1
		await this.setViewMode(searchTerm)
	}
}
